import { useMutation } from '@tanstack/react-query';
import { ColumnEvent } from 'primereact/column';
import { useCallback, useMemo, useState } from 'react';
import { TableApi } from '../../../services/modules/table';
import { useSelector } from 'react-redux';
import { RootState } from '../../../services/types/store-types';
import moment from 'moment';
import toastService from '../../../context/toast/api/service';

export const KdlRow = {
  counterCaption: 0,
  counterId: 1,
  serialNumber: 2,
  subscriber: 3,
  apartment: 4,
  house: 5,
  oldConsumption: 6,
  newConsumption: 7,
  date: 8, // ISO string
  time: 9, // HH:mm format
};

export interface IMessage {
  message_type: 'ok' | 'error';
  counter_message: string;
  counter_id: number;
}
export type MessagesMap = {
  [key: string]: IMessage;
};

export interface UseMassKdlLogicParams {
  initialRows: any[][];
  initialMessages?: MessagesMap;
  showMessage: (msg: {
    caption: string;
    message: string;
    type: 'info' | 'warn' | 'error' | 'succ';
    timeOut: number;
    tag: string;
  }) => void;
  closeEditingCell: () => void;
}

export const useMassKdlConsumption = ({
  initialRows,
  initialMessages,
  showMessage,
  closeEditingCell,
}: UseMassKdlLogicParams) => {
  const [rows, setRows] = useState<any[][]>(initialRows);
  const [messages, setMessages] = useState<MessagesMap | undefined>(
    initialMessages
  );
  const [edited, setEdited] = useState<Set<string>>(new Set());
  const { selectedNodes } = useSelector((state: RootState) => state.tree);

  // Находит сообщение по Id
  const messagesByCounterId = useMemo(() => {
    if (!messages) return {};
    return Object.values(messages).reduce((acc, msg) => {
      acc[msg.counter_id] = msg;
      return acc;
    }, {} as Record<number, IMessage>);
  }, [messages]);

  const getMessageById = useCallback(
    (id: number) => messagesByCounterId[id] || null,
    [messagesByCounterId]
  );

  const onCellEditInit = useCallback(
    (e: ColumnEvent) => {
      const { rowData, rowIndex } = e;
      setEdited((prevEdited) =>
        new Set(prevEdited).add(rows[rowIndex][KdlRow.counterId])
      );
      setRows((prevRows: any) => {
        const newRows = [...prevRows];
        const newArrayData = Object.values(rowData);
        newArrayData[KdlRow.date] = newArrayData[KdlRow.date] || new Date();
        newArrayData[KdlRow.time] = newArrayData[KdlRow.time] || new Date();
        newRows[rowIndex] = newArrayData;

        return newRows;
      });
    },
    [rows]
  );

  // Завершение редактирования ячейки — сохраняем изменения, если они произошли
  const onCellEditComplete = useCallback((e: ColumnEvent) => {
    const { rowIndex, newRowData } = e;
    setRows((prevRows: any) => {
      const newRows = [...prevRows];
      const newArrayData = Object.values(newRowData);
      if (prevRows[rowIndex].join() !== newArrayData.join()) {
        newRows[rowIndex] = newArrayData;
      }

      return newRows;
    });
  }, []);

  // Используем react-query для отправки обновлённых данных
  const mutation = useMutation({
    mutationFn: async (requestData: TableApi.TDataTableUpdIns[]) => {
      const objectId = selectedNodes.objectIds;
      const response = await TableApi.dataTableUpd({
        data: { table: { ins: requestData } },
        objectId,
        tableName: 'massKDLConsumption',
        tableCaption: 'Установка массовых показаний счётчиков на КДЛ',
      });

      return response;
    },
    onSuccess() {
      toastService.show({
        caption: 'Информация',
        message: 'Данные отправлены',
        type: 'info',
        timeOut: 3,
        tag: 'kdl',
      });
    },
  });

  // Обработка сабмита – завершаем редактирование, формируем данные и отправляем запрос
  const handleSubmit = useCallback(() => {
    if (document.activeElement) {
      (document.activeElement as HTMLElement).blur();
    }
    closeEditingCell();

    const updatedRows = rows.filter((item) =>
      edited.has(item[KdlRow.counterId])
    );

    if (updatedRows.length === 0) {
      showMessage({
        caption: 'Подсказка',
        message: 'Вы ничего не изменили',
        type: 'info',
        timeOut: 3,
        tag: 'kdl',
      });
      return;
    }

    const requestData = updatedRows.map((item) => {
      console.log(item, item[KdlRow.date]);
      return [
        item[KdlRow.counterId],
        item[KdlRow.newConsumption],
        (new Date(item[KdlRow.date]).getTime() / 1000).toFixed(0),
        moment(item[KdlRow.time]).format('HH:mm'),
      ];
    }) as unknown as TableApi.TDataTableUpdIns[];
    console.log(updatedRows, requestData);
    mutation.mutate(requestData, {
      onSuccess: () => {
        setEdited(new Set());
      },
    });
  }, [rows, edited, mutation, closeEditingCell, showMessage]);

  return {
    rows,
    setRows,
    messages,
    setMessages,
    getMessageById,
    onCellEditInit,
    onCellEditComplete,
    handleSubmit,
    isPending: mutation.isPending,
  };
};
